import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { T, PageHeader, Section, Field, Row, Badge } from '../components/UI';
import api from '../api/axios';

const STATUS_MAP = {
  submitted:   { label:'Submitted',   bg:'var(--green-bg)',   color:'var(--green)'  },
  pending_po:  { label:'Pending PO',  bg:'var(--amber-bg)',   color:'var(--amber)'  },
  po_received: { label:'PO Received', bg:'var(--blue-light)', color:'var(--blue)'   },
  sent_to_hr:  { label:'Sent to HR',  bg:'var(--purple-bg)',  color:'var(--purple)' },
  invoiced:    { label:'Invoiced',    bg:'var(--green-bg)',   color:'var(--green)'  },
};

export default function JobView() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [job,     setJob]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    api.get(`/jobs/${id}`)
      .then(({ data }) => setJob(data.job))
      .catch(err => setError(err.response?.data?.message || 'Failed to load job'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)', color:'var(--muted)', fontFamily:'Inter,sans-serif' }}>
      <i className="ti ti-loader" style={{ fontSize:'24px', color:'var(--blue)' }} aria-hidden="true"/>
    </div>
  );

  if (error || !job) return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', fontFamily:'Inter,sans-serif', padding:'40px', textAlign:'center', color:'var(--red)' }}>
      {error || 'Job not found'}
    </div>
  );

  const st = STATUS_MAP[job.status] || STATUS_MAP.submitted;

  return (
    <div style={T.page}>
      <PageHeader title={`Job — ${job.stockNum || 'View'}`} onBack={() => navigate(-1)}
        actions={
          <Badge label={st.label} color={st.color} bg={st.bg}/>
        }
      />
      <div style={{ maxWidth:'680px', margin:'0 auto', padding:'20px 16px 80px' }}>

        <Section title="Location & Dealership">
          <Row cols={2}>
            <Field label="Location">
              <div style={{ padding:'9px 12px', background:'var(--bg)', borderRadius:'var(--radius)', border:'1px solid var(--border)', fontSize:'14px', color:'var(--text)' }}>{job.location || '—'}</div>
            </Field>
            <Field label="Dealership">
              <div style={{ padding:'9px 12px', background:'var(--bg)', borderRadius:'var(--radius)', border:'1px solid var(--border)', fontSize:'14px', color:'var(--text)' }}>{job.dealership || '—'}</div>
            </Field>
          </Row>
        </Section>

        <Section title="Vehicle">
          <Row cols={3}>
            <Field label="Stock Number">
              <div style={{ padding:'9px 12px', background:'var(--bg)', borderRadius:'var(--radius)', border:'1px solid var(--border)', fontFamily:'JetBrains Mono,monospace', fontSize:'14px', fontWeight:'500', color:'var(--blue)' }}>{job.stockNum || '—'}</div>
            </Field>
            <Field label="VIN">
              <div style={{ padding:'9px 12px', background:'var(--bg)', borderRadius:'var(--radius)', border:'1px solid var(--border)', fontFamily:'JetBrains Mono,monospace', fontSize:'13px', color:'var(--muted)' }}>{job.vinNum || '—'}</div>
            </Field>
            <Field label="Rego">
              <div style={{ padding:'9px 12px', background:'var(--bg)', borderRadius:'var(--radius)', border:'1px solid var(--border)', fontFamily:'JetBrains Mono,monospace', fontSize:'13px', color:'var(--text)' }}>{job.rego || '—'}</div>
            </Field>
          </Row>
        </Section>

        <Section title="Description">
          <Field label="Description">
            <div style={{ padding:'9px 12px', background:'var(--bg)', borderRadius:'var(--radius)', border:'1px solid var(--border)', fontSize:'14px', color:'var(--text)' }}>{job.description || '—'}</div>
          </Field>
        </Section>

        <Section title="Services">
          <Field label="General Services">
            <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
              {(job.coreServices || []).length > 0
                ? job.coreServices.map(s => (
                    <span key={s} style={{ background:'var(--blue-light)', color:'var(--blue)', fontSize:'13px', fontWeight:'500', padding:'4px 12px', borderRadius:'20px' }}>{s}</span>
                  ))
                : <span style={{ color:'var(--muted)', fontSize:'13px' }}>No services</span>
              }
            </div>
          </Field>
          {job.extraNotes && (
            <Field label="Extra Notes">
              <div style={{ padding:'9px 12px', background:'var(--bg)', borderRadius:'var(--radius)', border:'1px solid var(--border)', fontSize:'14px', color:'var(--text)' }}>{job.extraNotes}</div>
            </Field>
          )}
          {job.delTo && (
            <Field label="Delivered To">
              <div style={{ padding:'9px 12px', background:'var(--bg)', borderRadius:'var(--radius)', border:'1px solid var(--border)', fontSize:'14px', color:'var(--text)' }}>{job.delTo}</div>
            </Field>
          )}
        </Section>

        <Section title="PO Entries">
          {(job.poEntries && job.poEntries.length > 0
            ? job.poEntries
            : [{ poNumber: job.poNumber||'', price: job.price||0, services: job.coreServices||[], invoiceNumber: job.invoiceNumber||'' }]
          ).map((entry, idx) => (
            <div key={idx} style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'14px', marginBottom:'10px' }}>
              {job.poEntries?.length > 1 && (
                <div style={{ fontSize:'11px', fontWeight:'500', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:'10px' }}>PO Entry {idx+1}</div>
              )}
              <Row cols={2}>
                <Field label="PO Number">
                  <div style={{ padding:'9px 12px', background:'var(--surface)', borderRadius:'var(--radius)', border:'1px solid var(--border)', fontFamily:'JetBrains Mono,monospace', fontSize:'14px', fontWeight:'600', color:'var(--green)' }}>
                    {entry.poNumber || <span style={{ color:'var(--amber)' }}>Pending</span>}
                  </div>
                </Field>
                <Field label="Price">
                  <div style={{ padding:'9px 12px', background:'var(--surface)', borderRadius:'var(--radius)', border:'1px solid var(--border)', fontFamily:'JetBrains Mono,monospace', fontSize:'14px', fontWeight:'600', color:'var(--green)' }}>
                    ${(entry.price || 0).toFixed(2)}
                  </div>
                </Field>
              </Row>
              {idx > 0 && entry.services?.length > 0 && (
                <Field label={`Services for PO ${idx+1}`}>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                    {entry.services.map(s => (
                      <span key={s} style={{ background:'var(--blue-light)', color:'var(--blue)', fontSize:'13px', fontWeight:'500', padding:'4px 12px', borderRadius:'20px' }}>{s}</span>
                    ))}
                  </div>
                </Field>
              )}
            </div>
          ))}
        </Section>

        <div style={{ fontSize:'12px', color:'var(--muted)', textAlign:'center', marginTop:'8px' }}>
          Submitted by {job.submittedByName || '—'} · {job.createdAt ? new Date(job.createdAt).toLocaleDateString('en-AU', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'}
        </div>
      </div>
    </div>
  );
}
